import debounce from 'lodash/debounce'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { css } from 'design'
import { Button } from 'primitives/button'

import { ApiErrorMessages } from '../../controls/errors/api_error_messages'
import { TextToTextarea } from '../../controls/inputs/text_to_textarea'
import { useAxios } from '../../hooks/use_axios'
import { useRefCallback } from '../../hooks/use_ref_callback'
import { destroyTask, updateTask } from '../../reduxes/slices/tasks/actions'
import apiRoutes from '../../routes/admin_api_routes.json'

type Props = {
  task: any // TODO: generate types from rails models
}

export const Task = ({ task }: Props) => {
  const dispatch = useDispatch()
  const axios = useAxios()
  const { id, name, status, topic_id: topicId } = task
  const [checked, setChecked] = useState<boolean>(status === 'checked')
  const statusRef = useRef<string>(status)
  const [containerElement, containerRefCallback] =
    useRefCallback<HTMLDivElement>()

  useEffect(() => {
    statusRef.current = status
  }, [status])

  useEffect(() => {
    setChecked(status === 'checked')
  }, [status])

  const toggleStatus = useMemo(
    () =>
      debounce(
        (taskId, newStatus) => {
          const promise: any = dispatch(
            axios(updateTask(topicId, taskId, { status: newStatus ? 1 : 0 })),
          )
          promise.catch(() => {
            setChecked(statusRef.current === 'checked')
          })
        },
        500,
        { leading: true },
      ),
    [axios, dispatch, topicId],
  )

  const handleCheckboxChange = (taskId: number) => {
    setChecked((prevState) => {
      const newState = !prevState
      toggleStatus(taskId, newState)
      return newState
    })
  }

  const handleBlurFactory =
    (taskId: number) => (event: React.FocusEvent<HTMLTextAreaElement>) => {
      const name = event.currentTarget.value
      if (name) {
        return dispatch(axios(updateTask(topicId, taskId, { name })))
      } else {
        return dispatch(axios(destroyTask(topicId, taskId)))
      }
    }

  return (
    <div ref={containerRefCallback} className={taskContainerStyles()}>
      <Button
        id={id}
        className={checkboxStyles({ checked })}
        onClick={() => handleCheckboxChange(id)}
      >
        <i className={'fa fa-check'} />
      </Button>
      <TextToTextarea
        actualValue={name}
        onBlur={handleBlurFactory(id)}
        keyCodesToBlurOn={['Enter']}
        buttonProps={{
          className: textToTextAreaStyles({ checked }),
        }}
        textareaProps={{
          className: textToTextAreaStyles(),
        }}
        maxRows={null}
      />
      <ApiErrorMessages
        list={[
          {
            httpMethod: 'put',
            route: apiRoutes.admin_api_v1_topic_task.path,
            element: containerElement,
            uniqueKey: id,
          },
          {
            httpMethod: 'delete',
            route: apiRoutes.admin_api_v1_topic_task.path,
            element: containerElement,
            uniqueKey: id,
          },
        ]}
      />
    </div>
  )
}

const taskContainerStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
})

const checkboxStyles = css({
  fontSize: '$1',
  marginRight: '$2',
  minHeight: 'unset',
  padding: '$1',
  borderColor: '$grey300',

  variants: {
    checked: {
      false: {
        i: {
          visibility: 'hidden',
        },
      },
    },
  },
})

const textToTextAreaStyles = css({
  fontSize: 14,
  lineHeight: '22px',
  padding: '0 $2',
  flexGrow: 1,
  whiteSpace: 'unset',
  height: '100%',
  wordBreak: 'break-word',
  overflow: 'hidden',

  variants: {
    checked: {
      true: {
        color: '$grey500',
        textDecoration: 'line-through',
      },
    },
  },
})
